/**
 * TC Today + Auto-Coordinator draft queue — Phase 2 (2026-07-05).
 * Client for /api/tc/* and /api/coordinator-drafts/*.
 */
import { apiFetch } from '../auth/api';

export interface TcDeadline {
  transaction_id: number;
  address: string | null;
  agent: string | null;
  deadline_key: string;
  label: string;
  due_ts: number;
  due_yyyymmdd: number;
  bucket: 'overdue' | 'today' | 'tomorrow' | 'upcoming';
  at_risk: boolean;
}

export interface TcDraftRecipient {
  id: number;
  name: string;
  email: string | null;
  phone: string | null;
}

export interface CoordinatorDraft {
  id: number;
  transaction_id: number;
  address: string | null;
  agent: string | null;
  kind: string;
  channel: 'email' | 'sms_group';
  subject: string | null;
  body: string;
  due_at: number | null;
  created_at: number;
  generated_attachments: string[];
  recipient_phones: string[];
  recipients: TcDraftRecipient[];
}

export interface TcTask {
  id: number;
  transaction_id: number;
  title: string;
  due_date: number | null;
  status: string;
  priority: string;
  assignee_user_id: number | null;
  address_street: string | null;
  assignee_first: string | null;
  assignee_last: string | null;
}

export interface TcToday {
  deadlines: TcDeadline[];
  drafts: CoordinatorDraft[];
  tasks_due: TcTask[];
  untagged_purposes: string[];
  config: { sms_configured: boolean; tc_user_id: number | null };
}

export function getTcToday(): Promise<TcToday> {
  return apiFetch('/api/tc/today');
}

/** Pending post-close referral drafts for the caller — Home card badge. */
export function getFollowupsCount(): Promise<{ count: number }> {
  return apiFetch('/api/tc/followups-count');
}

export interface TcSettings {
  tc_user_id: number | null;
  tc_name: string | null;
  tc_has_phone: boolean;
  tc_has_email: boolean;
}

export function getTcSettings(): Promise<TcSettings> {
  return apiFetch('/api/tc/settings');
}

export function setTcUser(tcUserId: number | null): Promise<{ ok: true }> {
  return apiFetch('/api/tc/settings', {
    method: 'POST',
    body: JSON.stringify({ tc_user_id: tcUserId }),
  });
}

export function updateCoordinatorDraft(
  id: number,
  body: {
    subject?: string;
    body?: string;
    recipient_contact_ids?: number[];
    recipient_phones?: string[];
  },
): Promise<{ ok: true }> {
  return apiFetch(`/api/coordinator-drafts/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export function discardCoordinatorDraft(id: number): Promise<{ ok: true }> {
  return apiFetch(`/api/coordinator-drafts/${id}/discard`, { method: 'POST' });
}

export function markCoordinatorDraftSent(
  id: number,
  emailSendId?: number,
): Promise<{ ok: true }> {
  return apiFetch(`/api/coordinator-drafts/${id}/mark-sent`, {
    method: 'POST',
    body: JSON.stringify(emailSendId ? { email_send_id: emailSendId } : {}),
  });
}

export function sendCoordinatorGroupText(
  id: number,
): Promise<{ ok: true; message_id: string; recipients: number }> {
  return apiFetch(`/api/coordinator-drafts/${id}/send-group`, { method: 'POST' });
}
